import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { useItemAvailability } from 'entities/CatalogItem/lib/useItemAvailability';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { CategoryLabel } from 'entities/Category';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { useGetItemDataUpdate } from 'features/CatalogItemCheckbox/lib/useGetItemDataUpdate';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import { getTermFromCode } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { FindReplacementLink } from 'shared/ui/FindReplacementLink/FindReplacementLink';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { UnavailableTooltipContent } from 'shared/ui/ItemAvailabilityModal';
import { SupplierBadgeWithTooltip } from 'shared/ui/Suppliers/ui/SupplierBadgeWithTooltip';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

import {
    INVENTORY_THUMB_PX,
    columnWidths,
    inventoryCategoryTextSx,
    inventoryDataCellSx,
    inventoryItemTitleSx,
    inventoryPriceTypographySx,
    inventoryUomCellTypographySx,
} from '../../lib/inventoryTableRowSpec';
import { InventoryMinOnHandCell } from '../InventoryTable/InventoryMinOnHandCell';

interface OrderInventoryTableRowProps {
    item: CatalogItem;
    canSeePrices: boolean;
    onExpandRow: () => void;
}

export const OrderInventoryTableRow = ({
    item,
    canSeePrices,
    onExpandRow,
}: OrderInventoryTableRowProps) => {
    const { t } = useTranslation();
    const catalogIdentity = useCatalogIdentity();
    const itemUid = getItemUid(item, catalogIdentity);
    const { isActive, idToEdit, itemIsInOrder, qty } = useGetItemDataUpdate({
        item,
    });
    const { openLightbox } = useImageLightbox();
    const { availabilityStatus, isAvailable } = useItemAvailability(
        item.proposals,
    );
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    return (
        <TableRow
            data-testclass="inventory-item-row"
            onClick={() => onExpandRow()}
            sx={{
                cursor: 'pointer',
                transform: 'translate(0)',
                clipPath: ' inset(0)',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
                borderRadius: '4px',
                '& td': {
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    '&:first-of-type': {
                        borderRadius: '4px 0 0 4px',
                    },
                    '&:last-of-type': {
                        borderRadius: '0 4px 4px 0',
                    },
                },
            }}
        >
            <TableCell
                sx={{
                    ...inventoryDataCellSx,
                    padding: '15px 16px',
                    width: columnWidths.item,
                    maxWidth: columnWidths.item,
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        minWidth: 0,
                    }}
                >
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            openLightbox(item.thumbnailUrl);
                        }}
                        sx={{
                            width: `${INVENTORY_THUMB_PX}px`,
                            height: `${INVENTORY_THUMB_PX}px`,
                            minWidth: `${INVENTORY_THUMB_PX}px`,
                            minHeight: `${INVENTORY_THUMB_PX}px`,
                            maxWidth: `${INVENTORY_THUMB_PX}px`,
                            maxHeight: `${INVENTORY_THUMB_PX}px`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            overflow: 'hidden',
                            '&:hover': {
                                div: {
                                    borderColor: '#bababa',
                                },
                            },
                        }}
                    >
                        <CatalogItemImage
                            src={item.thumbnailUrl}
                            height={`${INVENTORY_THUMB_PX}px`}
                            width={`${INVENTORY_THUMB_PX}px`}
                            showBorder={false}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            alignItems: 'flex-start',
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Typography
                            data-testclass="inventory-item-title"
                            sx={inventoryItemTitleSx}
                        >
                            {item.title}
                        </Typography>
                        {item.proposals?.length ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Open Sans',
                                        fontWeight: 400,
                                        fontSize: '11px',
                                        lineHeight: 'normal',
                                        color: 'rgba(0, 0, 0, 0.5)',
                                    }}
                                >
                                    {t('Carried by')}
                                </Typography>
                                <SupplierBadgeWithTooltip
                                    fullProposals={item.proposals}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </TableCell>
            <TableCell
                data-testclass="inventory-item-category"
                align="left"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.category,
                    boxSizing: 'border-box',
                }}
            >
                <CategoryLabel
                    categoryId={item.categoryId}
                    textSx={inventoryCategoryTextSx}
                />
            </TableCell>
            <TableCell
                data-testclass="inventory-item-uom"
                sx={{
                    ...inventoryDataCellSx,
                    boxSizing: 'border-box',
                    width: columnWidths.uom,
                }}
                align="center"
            >
                <Typography align="center" sx={inventoryUomCellTypographySx}>
                    {getTermFromCode(item.uom, true)}
                </Typography>
            </TableCell>
            <TableCell
                data-testclass="inventory-item-min-on-hand"
                sx={{
                    ...inventoryDataCellSx,
                    boxSizing: 'border-box',
                    width: columnWidths.minOnHand,
                }}
                align="center"
            >
                <InventoryMinOnHandCell
                    minAllowedInStock={item.minAllowedInStock}
                />
            </TableCell>
            {canSeePrices && (
                <TableCell
                    data-testclass="inventory-item-price"
                    sx={{
                        ...inventoryDataCellSx,
                        width: '84px',
                        maxWidth: '84px',
                        boxSizing: 'content-box',
                    }}
                    align="right"
                >
                    <Typography sx={inventoryPriceTypographySx}>
                        {getPrice(item.price)}
                    </Typography>
                </TableCell>
            )}
            <TableCell
                data-testclass="inventory-item-actions"
                align="right"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.orderAdd,
                    boxSizing: 'content-box',
                    paddingRight: '12px',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '20px',
                    }}
                >
                    {isAvailable ? (
                        <CatalogItemCheckbox
                            showQtyCheckbox
                            isActive={isActive}
                            quickEdit
                            qty={qty}
                            selectionKey={itemUid}
                            itemUid={itemUid}
                            catalogItemId={item.id}
                            itemIdToEdit={idToEdit}
                            isInOrder={itemIsInOrder}
                            source="inventory"
                        />
                    ) : (
                        <>
                            {!isMaintenanceTechnician && (
                                <FindReplacementLink />
                            )}
                            <DynamicTooltip
                                maxWidth={400}
                                interactive
                                containerStyle={{
                                    display: 'inline-flex',
                                }}
                                content={() => (
                                    <UnavailableTooltipContent
                                        availabilityStatus={availabilityStatus}
                                        hideReplacementLink
                                    />
                                )}
                            >
                                <IconButton
                                    data-testclass="item-suppliers-availability-info-icon"
                                    aria-label="Item availability info"
                                    size="small"
                                    sx={{ p: 0 }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: 'error.light',
                                        }}
                                    />
                                </IconButton>
                            </DynamicTooltip>
                        </>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
};
